import {
  Home, Users, BarChart2, Layout, TrendingUp, Settings,
  Activity, Bot, UserCircle, Grid, Zap, MessageSquare,
  HelpCircle, Image, Globe, CheckSquare, FileText, Building2, Target,
  Monitor, PlayCircle, Brain, User, CreditCard, Database, BookOpen, Shield,
  FolderOpen, Code, Layers, MessageCircle, Key, Package, Edit3, Search, Video, Sparkles, Trophy,
  Workflow, Upload, Trash2, Wrench, Factory, Briefcase, DollarSign, Gauge, Wand2, Tag, Languages, Eye, Swords,
  type LucideIcon
} from 'lucide-react';

import type { OfferingType } from '@/types/offering';

export interface NavigationSubItem {
  label: string;
  to?: string;
  icon?: LucideIcon;
  isHeader?: boolean;
  visibleFor?: OfferingType[] | 'all';
}

export interface NavigationItem {
  icon?: LucideIcon;
  label: string;
  to?: string;
  isSection?: boolean;
  subItems?: NavigationSubItem[];
  hint?: string;
  visibleFor?: OfferingType[] | 'all';
}

/**
 * Filter navigation items by offering type.
 * Items without visibleFor or with visibleFor='all' are always shown.
 */
export function filterNavByOfferingType(items: NavigationItem[], offeringType: OfferingType): NavigationItem[] {
  return items
    .filter(item => !item.visibleFor || item.visibleFor === 'all' || item.visibleFor.includes(offeringType))
    .map(item => {
      if (!item.subItems) return item;
      const filteredSubs = item.subItems.filter(
        sub => !sub.visibleFor || sub.visibleFor === 'all' || sub.visibleFor.includes(offeringType)
      );
      return { ...item, subItems: filteredSubs };
    });
}

// Site Owner Menu Items
export const siteOwnerItems: NavigationItem[] = [
  { label: 'Dashboard', isSection: true },
  { icon: Home, label: 'Home', to: '/dashboard', hint: 'Your command center - see how your site is performing' },
  { label: 'About the Business', isSection: true },
  { icon: TrendingUp, label: 'The Sector', to: '/about-business/sector', hint: 'Industry insights and trends for your business' },
  { icon: Package, label: 'The Offering', to: '/about-business/offering', hint: 'Your products, services, and content catalog' },
  { icon: Sparkles, label: 'Discovery', to: '/discovery', hint: 'Natural language search across your products and knowledge' },
  { icon: DollarSign, label: 'Demand Research', to: '/demand-research', hint: 'Category demand vs content readiness analysis' },
  { icon: Zap, label: 'Demand Synthesis', to: '/demand-synthesis', hint: 'Clustered demand map, gap queue, vocabulary gaps, money leaks' },
  { icon: Globe, label: 'Terrain Explorer', to: '/terrain-explorer', hint: 'Visual discovery of demand space — zoom from clusters to individual queries' },
  { icon: MessageCircle, label: 'Conversation Intel', to: '/conversation-intelligence', hint: 'Community conversations, sentiment, opportunities & experts' },
  { label: 'Agents', isSection: true },
  { icon: UserCircle, label: 'Meet the Crew', to: '/crew', hint: 'Meet your AI assistants and see what they\'re doing' },
  { icon: MessageSquare, label: 'Agents Insights', to: '/insights/messages', hint: 'Discover what your AI agents learned about visitors' },
  { icon: MessageCircle, label: 'Agent Conversations', to: '/agent-conversations', hint: 'Read actual conversations between visitors and AI' },
  { label: 'Customers & Growth', isSection: true },
  { icon: BarChart2, label: 'Analytics & Chatbot', to: '/analytics', hint: 'Track web traffic, chatbot conversations, and engagement metrics' },
  { icon: Users, label: 'Customer Segments', to: '/analytics/customer-segments', hint: 'See who your visitors are - buyers, browsers, researchers' },
  { icon: Layers, label: 'Segment Management', to: '/analytics/segment-management', hint: 'Define and customize your visitor groups' },
  { icon: BarChart2, label: 'Segment Analytics', to: '/analytics/segment-analytics', hint: 'Compare how different visitor groups perform' },
  { icon: Layout, label: 'Experiences', to: '/session-replays', hint: 'Watch how real visitors navigate your site' },
  { icon: Brain, label: 'Behavior', to: '/behavior', hint: 'Understand what visitors do and why they do it' },
  { icon: TrendingUp, label: 'Growth', to: '/growth', hint: 'Find opportunities to grow your business' },
  { label: 'Knowledge & Content', isSection: true },
  {
    icon: Search, label: 'Research Intelligence', to: '/research', hint: 'Explore Reddit research: topics, opinions, questions & gaps',
    subItems: [
      { label: 'AEO Research', to: '/research/aeo', icon: Brain },
      { label: 'Competitive Benchmark', to: '/research/competitive-benchmark', icon: Trophy },
      { label: 'Question Wheel', to: '/research/question-wheel', icon: HelpCircle },
      { label: 'Data Sources', to: '/research/data-sources', icon: Database },
      { label: 'Attribute Approval', to: '/research/attribute-approval', icon: CheckSquare },
      { label: 'Enrichment Review', to: '/research/enrichment-review', icon: Shield },
      { label: 'Bulk Correction', to: '/research/bulk-correction', icon: Wrench },
      { label: 'Sector Question Wheels', to: '/research/sector-wheels', icon: MessageCircle },
      { label: 'Answer Generation', to: '/answer-generation', icon: Sparkles },
      { label: 'Knowledge', to: '/knowledge-base', icon: BookOpen },
    ],
  },
  { icon: BookOpen, label: 'My Knowledge', to: '/knowledge-base', hint: 'Uploaded content: products, documents, and reviews' },
  { icon: HelpCircle, label: 'Q&A System', to: '/qa-system', hint: 'Manage AI-powered questions and answers for your site' },
  { icon: FileText, label: 'My Site Topics', to: '/my-topics', hint: 'Topics and themes found on your website' },
  { icon: Edit3, label: 'AI Articles', to: '/ai-articles', hint: 'AI-generated blog articles from topics' },
  { icon: FolderOpen, label: 'Feed Export', to: '/feed-export', hint: 'Export enriched product feeds with Q&A data', visibleFor: ['product', 'mixed'] as const },
  { icon: Settings, label: 'Content Settings', to: '/settings/content-config', hint: 'Configure tone, style, and rules for AI-generated content' },
  { icon: BookOpen, label: 'Domain Knowledge', to: '/domain-knowledge', hint: 'Manage knowledge rules for AI content generation' },
  { icon: CheckSquare, label: 'Content Review', to: '/content-review', hint: 'Review and approve AI-generated content' },
  { icon: Shield, label: 'Quality Audit', to: '/quality-audit', hint: 'Review data quality issues and content corrections' },
  { icon: Database, label: 'Data Quality', to: '/data-quality', hint: 'Unified view of data quality issues, scores, and corrections' },
  { icon: Tag, label: 'Semantic Gaps', to: '/semantic-gaps', hint: 'Map brand technologies to semantic values for enrichment' },
  { icon: Languages, label: 'Translations', to: '/translations', hint: 'Configure languages and manage multilingual AEO content' },
  { icon: Workflow, label: 'AEO Pipeline', to: '/pipeline', hint: 'Automated content generation with validation loops' },
  { icon: Sparkles, label: 'AEO Impact Demo', to: '/aeo-impact-demo', hint: 'Interactive demo showing AEO element impact on metrics' },
  { icon: TrendingUp, label: 'AEO Impact', to: '/aeo-impact', hint: 'Measure AEO impact on traffic, rankings, and conversions' },
  { label: 'Site Tools', isSection: true },
  { icon: Gauge, label: 'AEO Readiness', to: '/aeo-readiness', hint: 'Check site readiness for AEO content generation' },
  { icon: Wand2, label: 'AEO Onboarding', to: '/aeo-onboarding', hint: 'Wizard to prepare your site for AEO content generation' },
  { icon: Bot, label: 'Robot Diagnostics', to: '/robot-diagnostics', hint: 'Check how search robots see your site' },
  { icon: Activity, label: 'Crawl Intelligence', to: '/crawl-intelligence', hint: 'Bot traffic and AEO exposure metrics' },
  { icon: Globe, label: 'Deployment Verification', to: '/deployment-verification', hint: 'Verify if AEO FAQs are deployed on live pages' },
  { icon: DollarSign, label: 'Business KPIs', to: '/business-kpi', hint: 'Revenue, expenses, runway, and budget vs actuals' },
  { icon: Briefcase, label: 'Executive Dashboard', to: '/executive-dashboard', hint: 'Strategic value metrics and business impact' },
  { icon: Trophy, label: 'Value Evidence', to: '/value-evidence', hint: 'Quantified proof of PageMind impact across 10 dimensions' },
  { icon: Factory, label: 'AI Factory', to: '/factory-dashboard', hint: 'Production operations and content generation' },
  { icon: Target, label: 'Pipeline Tracker', to: '/pipeline-tracker', hint: 'Mission control for content pipeline across sites' },
  { icon: TrendingUp, label: 'Content ROI', to: '/content-roi', hint: 'Track ROI of AI-optimized content changes' },
  { icon: Eye, label: 'AI Share of Voice', to: '/sov-dashboard', hint: 'Track visibility across ChatGPT, Perplexity, Gemini, and Claude' },
  { icon: Swords, label: 'Competitive Landscape', to: '/competitive-landscape', hint: 'Actors, categories, and competitive position by market' },
  { icon: BarChart2, label: 'Google Intelligence', to: '/google-intelligence', hint: 'Unified view of GSC, Analytics, Ads, and Shopping' },
  { label: 'Sites & Structure', isSection: true },
  { icon: Globe, label: 'My Sites', to: '/my-sites', hint: 'Add, edit, or remove monitored websites' },
  { icon: Globe, label: 'Markets', to: '/settings/markets', hint: 'Configure target markets for keyword research' },
  { icon: Zap, label: 'Google Integrations', to: '/settings/integrations', hint: 'Connect Google Search Console, Analytics, and Ads' },
  { icon: Activity, label: 'Crawl Monitor', to: '/crawl-monitor', hint: 'Monitor and manage crawl sessions for your sites' },
  { icon: Search, label: 'SEO Tracking', to: '/seo-tracking', hint: 'Track keyword rankings and SERP performance' },
  { icon: TrendingUp, label: 'Product Visibility', to: '/seo/visibility', hint: 'Track product rankings in Google search results', visibleFor: ['product', 'mixed'] as const },
  { label: 'Developer', isSection: true },
  { icon: Key, label: 'API Management', to: '/developer/api', hint: 'Connect PageMind to your other tools' },
  { label: 'Administration', isSection: true },
  { icon: User, label: 'Account', to: '/account', hint: 'Your personal settings and preferences' },
  { icon: CreditCard, label: 'Licensing', to: '/account/licensing', hint: 'Manage your plan and payment details' },
  { label: 'Help & Learning', isSection: true },
  {
    icon: BookOpen, label: 'Learning Center', to: '/help', hint: 'Guides, tutorials, and best practices',
    subItems: [
      { label: 'FAQ', to: '/help/faq', icon: HelpCircle },
      { label: 'User Guides', to: '/help/guides', icon: FileText },
    ],
  },
];

// Content Manager Menu Items
export const contentManagerItems: NavigationItem[] = [
  { label: 'Dashboard', isSection: true },
  { icon: Home, label: 'Home', to: '/dashboard', hint: 'Your command center - see site health and action items' },
  { label: 'About the Business', isSection: true },
  { icon: TrendingUp, label: 'The Sector', to: '/about-business/sector', hint: 'Industry insights and trends for your business' },
  { icon: Package, label: 'The Offering', to: '/about-business/offering', hint: 'View and enrich your product catalog' },
  { icon: Sparkles, label: 'Discovery', to: '/discovery', hint: 'Natural language search across your products and knowledge' },
  { icon: DollarSign, label: 'Demand Research', to: '/demand-research', hint: 'Category demand vs content readiness analysis' },
  { icon: Zap, label: 'Demand Synthesis', to: '/demand-synthesis', hint: 'Clustered demand map, gap queue, vocabulary gaps, money leaks' },
  { icon: Globe, label: 'Terrain Explorer', to: '/terrain-explorer', hint: 'Visual discovery of demand space — zoom from clusters to individual queries' },
  { icon: MessageCircle, label: 'Conversation Intel', to: '/conversation-intelligence', hint: 'Community conversations, sentiment, opportunities & experts' },
  { label: 'Agents', isSection: true },
  { icon: UserCircle, label: 'Meet the Crew', to: '/crew', hint: 'Meet your AI assistants and see what they\'re doing' },
  { label: 'Review & Approval', isSection: true },
  { icon: CheckSquare, label: 'Content Review', to: '/content-review', hint: 'Review and approve AI-generated AEO content' },
  { icon: Shield, label: 'Quality Audit', to: '/quality-audit', hint: 'Review data quality issues and content corrections' },
  { icon: Database, label: 'Data Quality', to: '/data-quality', hint: 'Unified view of data quality issues, scores, and corrections' },
  { icon: Tag, label: 'Semantic Gaps', to: '/semantic-gaps', hint: 'Map brand technologies to semantic values for enrichment' },
  { icon: Languages, label: 'Translations', to: '/translations', hint: 'Configure languages and manage multilingual AEO content' },
  { icon: Shield, label: 'Enrichment Review', to: '/research/enrichment-review', hint: 'Review and approve AI-enriched products' },
  { icon: Wrench, label: 'Bulk Correction', to: '/research/bulk-correction', hint: 'Search and fix product data in bulk using natural language' },
  { icon: CheckSquare, label: 'Attribute Approval', to: '/research/attribute-approval', hint: 'Approve or filter feed attributes for content generation' },
  { icon: MessageSquare, label: 'My Suggestions', to: '/my-suggestions', hint: 'Track your submitted feedback and bug reports' },
  { icon: Workflow, label: 'AEO Pipeline', to: '/pipeline', hint: 'Automated content generation with validation loops' },
  { icon: TrendingUp, label: 'AEO Impact', to: '/aeo-impact', hint: 'Measure AEO impact on traffic, rankings, and conversions' },
  { label: 'Site Tools', isSection: true },
  { icon: Gauge, label: 'AEO Readiness', to: '/aeo-readiness', hint: 'Check site readiness for AEO content generation' },
  { icon: Wand2, label: 'AEO Onboarding', to: '/aeo-onboarding', hint: 'Wizard to prepare your site for AEO content generation' },
  { icon: Bot, label: 'Robot Diagnostics', to: '/robot-diagnostics', hint: 'Check how search robots see your site' },
  { icon: Activity, label: 'Crawl Intelligence', to: '/crawl-intelligence', hint: 'Bot traffic and AEO exposure metrics' },
  { icon: Globe, label: 'Deployment Verification', to: '/deployment-verification', hint: 'Verify if AEO FAQs are deployed on live pages' },
  { icon: TrendingUp, label: 'Product Visibility', to: '/seo/visibility', hint: 'Track product rankings in Google search results', visibleFor: ['product', 'mixed'] as const },
  { icon: Eye, label: 'AI Share of Voice', to: '/sov-dashboard', hint: 'Track visibility across ChatGPT, Perplexity, Gemini, and Claude' },
  { icon: Swords, label: 'Competitive Landscape', to: '/competitive-landscape', hint: 'Actors, categories, and competitive position by market' },
  { icon: Zap, label: 'Google Integrations', to: '/settings/integrations', hint: 'Connect Google Search Console, Analytics, and Ads' },
  { icon: DollarSign, label: 'Business KPIs', to: '/business-kpi', hint: 'Revenue, expenses, runway, and budget vs actuals' },
  { icon: Briefcase, label: 'Executive Dashboard', to: '/executive-dashboard', hint: 'Strategic value metrics and business impact' },
  { icon: Trophy, label: 'Value Evidence', to: '/value-evidence', hint: 'Quantified proof of PageMind impact across 10 dimensions' },
  { icon: Factory, label: 'AI Factory', to: '/factory-dashboard', hint: 'Production operations and content generation' },
  { icon: Target, label: 'Pipeline Tracker', to: '/pipeline-tracker', hint: 'Mission control for content pipeline across sites' },
  { icon: BarChart2, label: 'Google Intelligence', to: '/google-intelligence', hint: 'Unified view of GSC, Analytics, Ads, and Shopping' },
  { label: 'Content Creation', isSection: true },
  { icon: Settings, label: 'Content Settings', to: '/settings/content-config', hint: 'Configure tone, style, and rules for AI-generated content' },
  { icon: BookOpen, label: 'Domain Knowledge', to: '/domain-knowledge', hint: 'Manage knowledge rules for AI content generation' },
  { icon: Video, label: 'AI Journalist', to: '/admin/journalists', hint: 'Manage AI journalists for interviews' },
  { icon: MessageSquare, label: 'Content Interviews', to: '/admin/interviews', hint: 'View and manage content interviews' },
  { icon: Users, label: 'Experts Management', to: '/admin/experts', hint: 'Manage industry experts for content creation' },
  { icon: Edit3, label: 'AI Articles', to: '/ai-articles', hint: 'Create AI-powered blog content' },
  { label: 'Research', isSection: true },
  { icon: FileText, label: 'My Site Topics', to: '/my-topics', hint: 'Research topics relevant to your business' },
  { icon: Trophy, label: 'Competitive Benchmark', to: '/research/competitive-benchmark', hint: 'Compare your content against competitors' },
  { label: 'Exports', isSection: true },
  { icon: FolderOpen, label: 'Feed Export', to: '/feed-export', hint: 'Export enriched product data', visibleFor: ['product', 'mixed'] as const },
  { icon: Upload, label: 'Feed Imports', to: '/feed-imports', hint: 'Import and manage product feeds', visibleFor: ['product', 'mixed'] as const },
  { label: 'Account', isSection: true },
  { icon: User, label: 'My Account', to: '/account', hint: 'Your personal settings and preferences' },
  { label: 'Help & Learning', isSection: true },
  {
    icon: BookOpen, label: 'Learning Center', to: '/help', hint: 'Guides, tutorials, and best practices',
    subItems: [
      { label: 'FAQ', to: '/help/faq', icon: HelpCircle },
      { label: 'User Guides', to: '/help/guides', icon: FileText },
    ],
  },
];

// Super Admin Menu Items
export const superAdminItems: NavigationItem[] = [
  { label: 'Dashboard', isSection: true },
  { icon: Home, label: 'Home', to: '/dashboard', hint: 'Your command center - see how your site is performing' },
  { label: 'About the Business', isSection: true },
  { icon: TrendingUp, label: 'The Sector', to: '/about-business/sector', hint: 'Industry insights and trends for your business' },
  { icon: Package, label: 'The Offering', to: '/about-business/offering', hint: 'Your products, services, and content catalog' },
  { icon: Sparkles, label: 'Discovery', to: '/discovery', hint: 'Natural language search across your products and knowledge' },
  { icon: DollarSign, label: 'Demand Research', to: '/demand-research', hint: 'Category demand vs content readiness analysis' },
  { icon: Zap, label: 'Demand Synthesis', to: '/demand-synthesis', hint: 'Clustered demand map, gap queue, vocabulary gaps, money leaks' },
  { icon: Globe, label: 'Terrain Explorer', to: '/terrain-explorer', hint: 'Visual discovery of demand space — zoom from clusters to individual queries' },
  { icon: MessageCircle, label: 'Conversation Intel', to: '/conversation-intelligence', hint: 'Community conversations, sentiment, opportunities & experts' },
  { label: 'Agents', isSection: true },
  { icon: UserCircle, label: 'Meet the Crew', to: '/crew', hint: 'Meet your AI assistants and see what they\'re doing' },
  { icon: MessageSquare, label: 'Agents Insights', to: '/insights/messages', hint: 'Discover what your AI agents learned about visitors' },
  { icon: MessageCircle, label: 'Agent Conversations', to: '/agent-conversations', hint: 'Read actual conversations between visitors and AI' },
  { label: 'Customers & Growth', isSection: true },
  { icon: BarChart2, label: 'Analytics & Chatbot', to: '/analytics', hint: 'Track web traffic, chatbot conversations, and engagement metrics' },
  { icon: Users, label: 'Customer Segments', to: '/analytics/customer-segments', hint: 'See who your visitors are - buyers, browsers, researchers' },
  { icon: Layers, label: 'Segment Management', to: '/analytics/segment-management', hint: 'Define and customize your visitor groups' },
  { icon: BarChart2, label: 'Segment Analytics', to: '/analytics/segment-analytics', hint: 'Compare how different visitor groups perform' },
  { icon: Layout, label: 'Experiences', to: '/session-replays', hint: 'Watch how real visitors navigate your site' },
  { icon: Brain, label: 'Behavior', to: '/behavior', hint: 'Understand what visitors do and why they do it' },
  { icon: TrendingUp, label: 'Growth', to: '/growth', hint: 'Find opportunities to grow your business' },
  { label: 'Knowledge & Content', isSection: true },
  {
    icon: Search, label: 'Research Intelligence', to: '/research', hint: 'Explore Reddit research: topics, opinions, questions & gaps',
    subItems: [
      { label: 'AEO Research', to: '/research/aeo', icon: Brain },
      { label: 'Competitive Benchmark', to: '/research/competitive-benchmark', icon: Trophy },
      { label: 'Question Wheel', to: '/research/question-wheel', icon: HelpCircle },
      { label: 'Data Sources', to: '/research/data-sources', icon: Database },
      { label: 'Attribute Approval', to: '/research/attribute-approval', icon: CheckSquare },
      { label: 'Enrichment Review', to: '/research/enrichment-review', icon: Shield },
      { label: 'Bulk Correction', to: '/research/bulk-correction', icon: Wrench },
      { label: 'Sector Question Wheels', to: '/research/sector-wheels', icon: MessageCircle },
      { label: 'Answer Generation', to: '/answer-generation', icon: Sparkles },
      { label: 'Knowledge', to: '/knowledge-base', icon: BookOpen },
    ],
  },
  { icon: BookOpen, label: 'My Knowledge', to: '/knowledge-base', hint: 'Uploaded content: products, documents, and reviews' },
  { icon: HelpCircle, label: 'Q&A System', to: '/qa-system', hint: 'Manage AI-powered questions and answers for your site' },
  { icon: FileText, label: 'My Site Topics', to: '/my-topics', hint: 'Topics and themes found on your website' },
  { icon: Edit3, label: 'AI Articles', to: '/ai-articles', hint: 'AI-generated blog articles from topics' },
  { icon: FolderOpen, label: 'Feed Export', to: '/feed-export', hint: 'Export enriched product feeds with Q&A data', visibleFor: ['product', 'mixed'] as const },
  { icon: Upload, label: 'Feed Imports', to: '/feed-imports', hint: 'Import and manage product feeds', visibleFor: ['product', 'mixed'] as const },
  { icon: Settings, label: 'Content Settings', to: '/settings/content-config', hint: 'Configure tone, style, and rules for AI-generated content' },
  { icon: BookOpen, label: 'Domain Knowledge', to: '/domain-knowledge', hint: 'Manage knowledge rules for AI content generation' },
  { icon: CheckSquare, label: 'Content Review', to: '/content-review', hint: 'Review and approve AI-generated content' },
  { icon: Shield, label: 'Quality Audit', to: '/quality-audit', hint: 'Review data quality issues and content corrections' },
  { icon: Database, label: 'Data Quality', to: '/data-quality', hint: 'Unified view of data quality issues, scores, and corrections' },
  { icon: Tag, label: 'Semantic Gaps', to: '/semantic-gaps', hint: 'Map brand technologies to semantic values for enrichment' },
  { icon: Languages, label: 'Translations', to: '/translations', hint: 'Configure languages and manage multilingual AEO content' },
  { icon: Workflow, label: 'AEO Pipeline', to: '/pipeline', hint: 'Automated content generation with validation loops' },
  { icon: Sparkles, label: 'AEO Impact Demo', to: '/aeo-impact-demo', hint: 'Interactive demo showing AEO element impact on metrics' },
  { icon: TrendingUp, label: 'AEO Impact', to: '/aeo-impact', hint: 'Measure AEO impact on traffic, rankings, and conversions' },
  { label: 'Site Tools', isSection: true },
  { icon: Gauge, label: 'AEO Readiness', to: '/aeo-readiness', hint: 'Check site readiness for AEO content generation' },
  { icon: Wand2, label: 'AEO Onboarding', to: '/aeo-onboarding', hint: 'Wizard to prepare your site for AEO content generation' },
  { icon: Bot, label: 'Robot Diagnostics', to: '/robot-diagnostics', hint: 'Check how search robots see your site' },
  { icon: Activity, label: 'Crawl Intelligence', to: '/crawl-intelligence', hint: 'Bot traffic and AEO exposure metrics' },
  { icon: Globe, label: 'Deployment Verification', to: '/deployment-verification', hint: 'Verify if AEO FAQs are deployed on live pages' },
  { icon: DollarSign, label: 'Business KPIs', to: '/business-kpi', hint: 'Revenue, expenses, runway, and budget vs actuals' },
  { icon: Briefcase, label: 'Executive Dashboard', to: '/executive-dashboard', hint: 'Strategic value metrics and business impact' },
  { icon: Trophy, label: 'Value Evidence', to: '/value-evidence', hint: 'Quantified proof of PageMind impact across 10 dimensions' },
  { icon: Factory, label: 'AI Factory', to: '/factory-dashboard', hint: 'Production operations and content generation' },
  { icon: Target, label: 'Pipeline Tracker', to: '/pipeline-tracker', hint: 'Mission control for content pipeline across sites' },
  { icon: TrendingUp, label: 'Content ROI', to: '/content-roi', hint: 'Track ROI of AI-optimized content changes' },
  { icon: Eye, label: 'AI Share of Voice', to: '/sov-dashboard', hint: 'Track visibility across ChatGPT, Perplexity, Gemini, and Claude' },
  { icon: Swords, label: 'Competitive Landscape', to: '/competitive-landscape', hint: 'Actors, categories, and competitive position by market' },
  { icon: BarChart2, label: 'Google Intelligence', to: '/google-intelligence', hint: 'Unified view of GSC, Analytics, Ads, and Shopping' },
  { label: 'Sites & Structure', isSection: true },
  { icon: Globe, label: 'My Sites', to: '/my-sites', hint: 'Add, edit, or remove monitored websites' },
  { icon: Globe, label: 'Markets', to: '/settings/markets', hint: 'Configure target markets for keyword research' },
  { icon: Zap, label: 'Google Integrations', to: '/settings/integrations', hint: 'Connect Google Search Console, Analytics, and Ads' },
  { icon: Activity, label: 'Crawl Monitor', to: '/crawl-monitor', hint: 'Monitor and manage crawl sessions for your sites' },
  { icon: Search, label: 'SEO Tracking', to: '/seo-tracking', hint: 'Track keyword rankings and SERP performance' },
  { icon: TrendingUp, label: 'Product Visibility', to: '/seo/visibility', hint: 'Track product rankings in Google search results', visibleFor: ['product', 'mixed'] as const },
  { label: 'Developer', isSection: true },
  { icon: Key, label: 'API Management', to: '/developer/api', hint: 'Connect PageMind to your other tools' },
  { label: 'Administration', isSection: true },
  { icon: User, label: 'Account', to: '/account', hint: 'Your personal settings and preferences' },
  { icon: CreditCard, label: 'Licensing', to: '/account/licensing', hint: 'Manage your plan and payment details' },
  {
    icon: Shield, label: 'Super Admin', to: '/admin', hint: 'Advanced controls for system administrators',
    subItems: [
      { label: 'Setup & Structure', isHeader: true },
      { icon: Globe, label: 'Sites Manager', to: '/admin/sites' },
      { icon: Building2, label: 'Sectors', to: '/admin/sectors' },
      { icon: Sparkles, label: 'Research Management', to: '/admin/research-management' },
      { icon: Layers, label: 'Category Knowledge', to: '/admin/category-research' },
      { icon: CheckSquare, label: 'Master Topics Approval', to: '/admin/topics' },
      { label: 'Agents', isHeader: true },
      { icon: Grid, label: 'Agent Dashboard', to: '/agents/dashboard' },
      { icon: Monitor, label: 'Agent Monitoring', to: '/admin/agent-monitoring' },
      { icon: CreditCard, label: 'License Management', to: '/admin/licenses' },
      { icon: CreditCard, label: 'Subscription Management', to: '/admin/subscriptions' },
      { icon: Globe, label: 'Source Domains', to: '/admin/source-domains' },
      { icon: Image, label: 'Image Generation', to: '/admin/image-generation' },
      { icon: Users, label: 'AI Journalists', to: '/admin/journalists' },
      { icon: Video, label: 'Content Interviews', to: '/admin/interviews' },
      { icon: Users, label: 'Experts Management', to: '/admin/experts' },
      { icon: Users, label: 'Waitlist Management', to: '/admin/waitlist' },
      { icon: Shield, label: 'Site Permissions', to: '/admin/site-permissions' },
      { label: 'Live Operations', isHeader: true },
      { icon: Activity, label: 'Customer Simulator', to: '/admin/customer-simulator' },
      { icon: Activity, label: 'Active Sessions', to: '/admin/active-sessions' },
      { icon: PlayCircle, label: 'Session Monitoring', to: '/monitoring' },
      { label: 'Knowledge & Logs', isHeader: true },
      { icon: MessageSquare, label: 'Platform Suggestions', to: '/admin/suggestions' },
      { icon: BookOpen, label: 'Research Documents', to: '/admin/research-documents' },
      { icon: Database, label: 'Chatbot Logs', to: '/admin/chatbot-logs' },
      { icon: Activity, label: 'Chatbot Performance', to: '/admin/chatbot-performance' },
      { icon: Activity, label: 'Search Performance', to: '/admin/search-performance' },
      { label: 'Platform Health', isHeader: true },
      { icon: BarChart2, label: 'Grafana', to: '/admin/grafana' },
      { icon: Activity, label: 'Prometheus', to: '/admin/prometheus' },
      { icon: Database, label: 'ETL Monitoring', to: '/analytics/etl' },
      { icon: MessageSquare, label: 'Kafka Queue Monitor', to: '/admin/kafka-monitoring' },
      { icon: Trash2, label: 'Data Management', to: '/admin/data-management' },
      { label: 'Developer Tools', isHeader: true },
      { icon: Code, label: 'Tools & Docs', to: '/admin/developer-tools' },
    ],
  },
  { label: 'Help & Learning', isSection: true },
  {
    icon: BookOpen, label: 'Learning Center', to: '/help', hint: 'Guides, tutorials, and best practices',
    subItems: [
      { label: 'FAQ', to: '/help/faq', icon: HelpCircle },
      { label: 'User Guides', to: '/help/guides', icon: FileText },
    ],
  },
];

// Helper function to get items for a role
export function getNavigationItemsForRole(role: string | undefined): NavigationItem[] {
  if (role === 'super_admin') return superAdminItems;
  if (role === 'site_content_manager') return contentManagerItems;
  return siteOwnerItems;
}

// Flatten navigation items into searchable list
export interface SearchableNavItem {
  label: string;
  to: string;
  icon?: LucideIcon;
  hint?: string;
  section?: string;
  parentLabel?: string;
}

export function getSearchableItems(items: NavigationItem[]): SearchableNavItem[] {
  const searchable: SearchableNavItem[] = [];
  let currentSection = '';

  for (const item of items) {
    if (item.isSection) {
      currentSection = item.label;
      continue;
    }

    // Add main item if it has a route
    if (item.to) {
      searchable.push({
        label: item.label,
        to: item.to,
        icon: item.icon,
        hint: item.hint,
        section: currentSection,
      });
    }

    // Add sub-items
    if (item.subItems) {
      for (const subItem of item.subItems) {
        if (subItem.to && !subItem.isHeader) {
          searchable.push({
            label: subItem.label,
            to: subItem.to,
            icon: subItem.icon,
            hint: undefined,
            section: currentSection,
            parentLabel: item.label,
          });
        }
      }
    }
  }

  return searchable;
}
